import { useQuery } from "@tanstack/react-query";
import { CampaignService } from "@/services/campaign/campaignService";
import { GetCampaignResponse } from "@/types/campaign";

export const useCampaigns = (
  page: number = 1,
  limit: number = 10,
  options?: { enabled?: boolean },
) => {
  // Get all campaigns query
  const getAllCampaignsQuery = useQuery({
    queryKey: ['campaigns', page, limit],
    queryFn: () => CampaignService.getAllCampaigns(page, limit),
    enabled: options?.enabled !== false,
    // staleTime: 5 * 60 * 1000,
    // gcTime: 10 * 60 * 1000,
  });

  return {
    // Get all campaigns
    campaigns: getAllCampaignsQuery.data?.campaigns || [],
    totalCampaigns: getAllCampaignsQuery.data?.total || 0,
    isLoadingCampaigns: getAllCampaignsQuery.isLoading,
    campaignsError: getAllCampaignsQuery.error,
    refetchCampaigns: getAllCampaignsQuery.refetch,
  };
};

export const useCampaign = (campaignId: string, options?: { enabled?: boolean }) => {
  // Get specific campaign with listings
  const getCampaignQuery = useQuery<GetCampaignResponse>({
    queryKey: ['campaign', campaignId],
    queryFn: () => CampaignService.getCampaign(campaignId),
    enabled: !!campaignId && options?.enabled !== false, // Only fetch if campaignId is provided
    // staleTime: 5 * 60 * 1000,
    // gcTime: 10 * 60 * 1000,
  });

  return {
    // Get campaign
    campaign: getCampaignQuery.data?.campaign,
    listings: getCampaignQuery.data?.listings || [],
    totalListings: getCampaignQuery.data?.totalListings || 0,
    isLoadingCampaign: getCampaignQuery.isLoading,
    campaignError: getCampaignQuery.error,
    refetchCampaign: getCampaignQuery.refetch,
  };
};

export const usePublicCampaign = (campaignId: string) => {
  const getPublicCampaignQuery = useQuery({
    queryKey: ["campaign", "public", campaignId],
    queryFn: () => CampaignService.getPublicCampaign(campaignId),
    enabled: !!campaignId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    campaign: getPublicCampaignQuery.data?.campaign,
    listings: getPublicCampaignQuery.data?.listings || [],
    totalListings: getPublicCampaignQuery.data?.totalListings || 0,
    isLoadingCampaign: getPublicCampaignQuery.isLoading,
    campaignError: getPublicCampaignQuery.error,
    refetchCampaign: getPublicCampaignQuery.refetch,
  };
};

export const usePublicAuctionCampaigns = (limit: number = 4) => {
  // Get public auction campaigns (unprotected endpoint)
  const getPublicAuctionCampaignsQuery = useQuery({
    queryKey: ['publicAuctionCampaigns', limit],
    queryFn: () => CampaignService.getPublicAuctionCampaigns(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    publicAuctionCampaigns: getPublicAuctionCampaignsQuery.data || [],
    isLoadingPublicAuctionCampaigns: getPublicAuctionCampaignsQuery.isLoading,
    publicAuctionCampaignsError: getPublicAuctionCampaignsQuery.error,
    refetchPublicAuctionCampaigns: getPublicAuctionCampaignsQuery.refetch,
  };
};